import {
  EntryType,
  OptionType,
  SchemaVariant,
  type OptionEntry,
  type TreeNode,
} from '@morph-mapper/types';
import { LOGIC_BLOCKS_RULESET, type BlockRule, type LogicBlockRecord } from '@morph-mapper/node-logic';
import type { ChatIntent } from '@/store';
import type { Graph, NodeId } from '../../../graph-editor/types';
import { TERMINAL_NODE_ID } from '../../../graph-editor/config/graph';
import { fieldPath } from './open-field';

type Blocks = LogicBlockRecord<any, Set<BlockRule>>;

// How much of the prompt the graphs may take, so a large template still fits.
export const MAX_GRAPH_CHARS = 8000;

/** An option's value as the assistant reads it, or undefined when unset. */
const optionValue = (option: OptionEntry<unknown> | undefined): unknown => {
  if (!option) return undefined;
  if (option.type === OptionType.Graph) return option.graphId ? '(set by a sub-graph)' : undefined;
  return option.value;
};

/**
 * A field's graph as text, from the output down: one node per line, with its
 * kind, the options that are set and the node (and port) it connects to.
 * "output" is the field itself.
 */
export const describeGraph = (graph: Graph): string[] => {
  const edges = Object.values(graph.edges);
  const lines: string[] = [];
  const seen = new Set<NodeId>();

  /** A node's id, kind and the options that are set. */
  const describeNode = (node: Graph['nodes'][string]) => {
    const options = Object.fromEntries(
      Object.entries(node.options ?? {})
        .map(([key, option]) => [key, optionValue(option as OptionEntry<unknown>)])
        .filter(([, value]) => value !== undefined && value !== '')
    );
    const shown = Object.keys(options).length > 0 ? ` ${JSON.stringify(options)}` : '';
    return `${node.id} ${node.kind}${shown}`;
  };

  const visit = (nodeId: NodeId, depth: number) => {
    const children = edges
      .filter((edge) => edge.to.nodeId === nodeId)
      .sort((a, b) => a.to.portId.localeCompare(b.to.portId) || a.order - b.order);
    for (const edge of children) {
      const child = graph.nodes[edge.from.nodeId];
      if (!child || seen.has(child.id)) continue;
      seen.add(child.id);

      const target = nodeId === TERMINAL_NODE_ID ? 'output' : `${nodeId}.${edge.to.portId}`;
      lines.push(`${'  '.repeat(depth)}- ${describeNode(child)} → ${target}`);
      visit(child.id, depth + 1);
    }
  };

  visit(TERMINAL_NODE_ID, 0);

  // Nodes that aren't connected to the output still show, so they can be fixed.
  for (const node of Object.values(graph.nodes)) {
    if (node.id === TERMINAL_NODE_ID || seen.has(node.id)) continue;
    lines.push(`- ${describeNode(node)} → (not connected)`);
  }
  return lines;
};

/**
 * The graphs of the template's graph fields, for the mapping assistant: the
 * open field first, then the others in template order, up to MAX_GRAPH_CHARS.
 */
export const graphsContextFor = (
  intent: ChatIntent | null,
  entries: Record<string, TreeNode>,
  getGraph: (graphId: string) => Graph | undefined,
  openEntryId?: string
): string | undefined => {
  if (intent !== 'mapping-assistant') return undefined;

  const graphFields = Object.values(entries).filter(
    (entry) => entry.type === EntryType.Graph && entry.graphId
  );
  graphFields.sort((a, b) => Number(b.id === openEntryId) - Number(a.id === openEntryId));

  const parts: string[] = [];
  let used = 0;
  for (const entry of graphFields) {
    if (entry.type !== EntryType.Graph || !entry.graphId) continue;
    const graph = getGraph(entry.graphId);
    if (!graph) continue;

    const lines = describeGraph(graph);
    const part = [`${fieldPath(entries, entry.id)}:`, ...(lines.length ? lines : ['(empty)'])].join('\n');
    if (used + part.length > MAX_GRAPH_CHARS) {
      parts.push('… (more graphs, left out to save space)');
      break;
    }
    parts.push(part);
    used += part.length;
  }
  return parts.length > 0 ? parts.join('\n\n') : undefined;
};

/** A zod type in plain words: text, number, yes/no, a list, or the options. */
const describeType = (schema: unknown): string => {
  const def = (schema as { _def?: Record<string, unknown> } | undefined)?._def;
  switch (def?.typeName) {
    case 'ZodOptional':
    case 'ZodNullable':
    case 'ZodDefault':
      return describeType(def.innerType);
    case 'ZodString':
      return 'text';
    case 'ZodNumber':
      return 'number';
    case 'ZodBoolean':
      return 'yes/no';
    case 'ZodEnum':
      return `one of ${(def.values as string[]).join('|')}`;
    case 'ZodNativeEnum':
      return `one of ${Object.values(def.values as object).join('|')}`;
    case 'ZodArray':
      return `list of ${describeType(def.type)}`;
    case 'ZodLiteral':
      return JSON.stringify(def.value);
    default:
      return 'value';
  }
};

type OptionConfig = {
  type: unknown;
  description?: string;
  conditions?: { isHidden?: boolean; isConnector?: boolean };
};

/**
 * An option as the assistant reads it: its key, its type and what it does. An
 * option that takes a sub-graph is marked, as a change can't set it.
 */
const describeOption = (key: string, option: OptionConfig, translate: (key: string) => string) => {
  const type = option.conditions?.isConnector ? 'sub-graph' : describeType(option.type);
  // A description without a translation comes back as its key; it says nothing.
  const description = option.description ? translate(option.description) : '';
  const shown = description && description !== option.description ? `: ${description.replace(/\.+$/, '')}` : '';
  return `${key} (${type})${shown}`;
};

/**
 * The node types the assistant can use, one per line: kind, title and what it
 * does, its inputs (ports) and its options with their types and what they do.
 * Titles and descriptions are translation keys, so they go through `translate`.
 */
export const nodeCatalogue = (blocks: Blocks, translate: (key: string) => string): string =>
  Object.entries(blocks)
    .filter(([, block]) => block.category !== 'internal')
    .map(([kind, block]) => {
      const inputs = (block.ports?.input ?? []).map(
        (port: { id: string; multi?: boolean }) => `${port.id}${port.multi ? ' (several)' : ''}`
      );
      const options = Object.entries(block.options ?? {})
        .filter(([, option]) => !(option as OptionConfig).conditions?.isHidden)
        .map(([key, option]) => describeOption(key, option as OptionConfig, translate));
      return [
        `${kind}: ${translate(block.title)}. ${translate(block.description)}`,
        `inputs: ${inputs.length ? inputs.join(', ') : 'none'}`,
        `options: ${options.length ? options.join('; ') : 'none'}`,
      ].join(' | ');
    })
    .join('\n');

// How an anchor points at a value, for file types where that is a path the
// user picks in the file viewer: it starts at the file itself.
const ANCHOR_PATHS: Partial<Record<SchemaVariant, string>> = {
  [SchemaVariant.Json]:
    'In this JSON file, the selected option of an anchor is the path to the value, starting with $inputdocument: for {"order": {"reference": "A1"}}, $inputdocument.order.reference gives A1.',
};

/**
 * The node types for the mapping assistant, and how an anchor points at a
 * value in this file type. There are none before a template is loaded, as
 * they come with its file type.
 */
export const catalogueFor = (
  intent: ChatIntent | null,
  variant: SchemaVariant | undefined,
  translate: (key: string) => string
): string | undefined => {
  if (intent !== 'mapping-assistant' || !variant) return undefined;
  const catalogue = nodeCatalogue(LOGIC_BLOCKS_RULESET[variant] as Blocks, translate);
  const anchorPath = ANCHOR_PATHS[variant];
  return anchorPath ? `${catalogue}\n${anchorPath}` : catalogue;
};
